<template>
  <div id="warehouse-schedule-settings-section" class="padding-b-15px">
    <el-form :model="editForm" ref="editForm" :rules="editFormRules">
      <el-table
        stripe
        id="warehouse-schedule-list"
        size="mini"
        tooltip-effect="dark"
        sortable
        width="80%"
        class="dark-blue-table"
        :empty-text="$t('common.notAbleToFindRecords')"
        :data="warehouse.detail.warehouseSchedule"
      >
        <el-table-column
          prop="day"
          :label="$t('warehouse.settings.scheduleTableDayLabel')"
          align="center"
          min-width="100"
        >
          <template slot-scope="scope">
            <span>{{ scope.row.day }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="dataEntryCutOff"
          :label="$t('warehouse.settings.scheduleDataEntryCutoff')"
          align="center"
          min-width="100"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="cutOff">
                <el-time-select
                  class="inline-input padding-left-30"
                  size="mini"
                  style="width: 100%"
                  clearable
                  v-model="editForm.cutOff"
                  :picker-options="datePickerHourOptions"
                ></el-time-select>
              </el-form-item>
            </template>
            <template v-else>
              <span>{{ scope.row.dataEntryCutOff }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="automaticStartDay"
          :label="$t('warehouse.settings.scheduleAutomaticStartDay')"
          align="center"
          min-width="100"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="cutOff">
                <el-time-select
                  class="inline-input padding-left-30"
                  size="mini"
                  style="width: 100%"
                  clearable
                  v-model="editForm.startDay"
                  :picker-options="datePickerStartDayHourOptions"
                ></el-time-select>
              </el-form-item>
            </template>
            <template v-else>
              <span>{{ scope.row.automaticStartDay }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('warehouse.settings.loadingSpeedCh')"
          align="center"
          min-width="100"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="loadingSpeed">
                <el-input
                  class="inline-input width-100p"
                  size="mini"
                  ref="loadingSpeed"
                  clearable
                  v-model="editForm.loadingSpeed"
                  v-numeric-validation
                ></el-input>
              </el-form-item>
            </template>
            <template v-else>
              <span>{{ scope.row.loadingSpeed }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.options')" align="center" min-width="100">
          <template slot-scope="scope">
            <el-tooltip
              v-if="showEditButton(scope.row)"
              class="item"
              effect="dark"
              :enterable="false"
              :content="$t('warehouse.orderEntry.edit')"
              placement="top-start"
            >
              <i class="el-icon-edit icon-action" @click="handleEdit(scope.row)"></i>
            </el-tooltip>
            <template v-else-if="scope.row.edit">
              <el-button icon="el-icon-success" circle size="mini" @click="handleUpdate(scope.row)"></el-button>
              <el-button
                icon="el-icon-circle-close"
                circle
                size="mini"
                @click="cancelEdit(scope.row)"
              ></el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </el-form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Message } from 'element-ui';
import moment from 'moment';
import constants from '@/utils/constants';

const warehouseSetEditingCutOffAction = 'warehouse/setEditingCutOff';
export default {
  name: 'scheduleWarehouseSettings',
  computed: {
    ...mapGetters(['warehouse', 'user']),
    minLoadingSpeed() {
      return constants.WAREHOUSE_SCHEDULE_SETTINGS.MIN_LOADING_SPEED;
    },
    maxLoadingSpeed() {
      return constants.WAREHOUSE_SCHEDULE_SETTINGS.MAX_LOADING_SPEED;
    }
  },
  data() {
    const validateLoadingSpeed = (rule, value, callback) => {
      if (!value && this.editForm.cutOff) {
        callback(new Error(this.$t('warehouse.settings.error.loadingSpeedRequired')));
      } else if (!Number.isInteger(Number(value)) || Number(value) < this.minLoadingSpeed || Number(value) > this.maxLoadingSpeed) {
        callback(new Error(this.$t('warehouse.settings.error.loadingSpeedValid').replace('[minValue]', this.minLoadingSpeed).replace('[maxValue]', this.maxLoadingSpeed)));
      } else {
        callback();
      }
    };
    return {
      editForm: {
        cutOff: null,
        startDay: null,
        loadingSpeed: null
      },
      editFormRules: {
        loadingSpeed: [
          {
            validator: validateLoadingSpeed,
            trigger: 'blur'
          }
        ]
      },
      datePickerHourOptions: {
        step: '00:30',
        start: '00:00',
        end: '23:59'
      },
      datePickerStartDayHourOptions: {
        step: '00:30',
        start: '00:00',
        end: '8:29'
      }
    };
  },
  methods: {
    getWarehouseDetail() {
      this.$store.dispatch('warehouse/getWarehouseDetail');
    },
    handleEdit(row) {
      if (row.dataEntryCutOff) {
        this.editForm.cutOff = moment(row.dataEntryCutOff, [
          moment.ISO_8601,
          'HH:mm'
        ]).format('HH:mm');
      } else {
        this.editForm.cutOff = null;
      }
      if (row.automaticStartDay) {
        this.editForm.startDay = moment(row.automaticStartDay, [
          moment.ISO_8601,
          'HH:mm'
        ]).format('HH:mm');
      } else {
        this.editForm.startDay = null;
      }
      this.editForm.loadingSpeed = row.loadingSpeed;
      this.$store.dispatch(warehouseSetEditingCutOffAction, true);
      row.edit = true;
    },
    cancelEdit(row) {
      this.$store.dispatch(warehouseSetEditingCutOffAction, false);
      row.edit = false;
    },
    handleUpdate(row) {
      this.$refs.editForm.validate((valid, validationsErrors) => {
        if (valid) {
          Message.closeAll();
          const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
          const schedule = {
            cutOffDay: row.day,
            cutoffWithTimeZone: {
              hour: this.editForm.cutOff,
            },
            startDayTimeZone: {
              hour: this.editForm.startDay,
            },
            loadingSpeed: {
              id: row.loadingSpeedId,
              warehouseCutoffId: row.warehouseCutoffId,
              warehouseId: this.user.warehouseId,
              day: row.dayNumber,
              loadingSpeed: this.editForm.loadingSpeed
            }
          };
          this.$store
            .dispatch('warehouse/updateSchedule', schedule)
            .then((response) => {
              loading.close();
              this.$store.dispatch(warehouseSetEditingCutOffAction, false);
              this.$message.success(response.message);
            })
            .catch(() => {
              loading.close();
            });
        } else {
          let errorMessage = '';
          Object.keys(validationsErrors).forEach((key) => {
            validationsErrors[key].forEach((validation) => {
              errorMessage += `${validation.message}. `;
            });
          });
          this.$message.warning(errorMessage);
        }
      });
    },
    showEditButton(row) {
      return (
        this.$can(this.permissions.WAREHOUSE.EDIT_SETTINGS)
        && !this.warehouse.isEditingCutOff
        && !row.edit
      );
    }
  }
};
</script>

<style rel="stylesheet/scss" lang="scss">
#warehouse-schedule-settings-section {
  .dark-blue-table.el-table tbody tr td {
    .cell .padding-left-30 input {
      padding-left: 30px;
    }
  }
  .el-input--mini {
    .el-input__icon {
      line-height: 25px;
    }
  }
  .el-input-number--mini {
    line-height: 24px;
  }
}
</style>
